import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SelectWithCreate } from '../../components/ui/select-with-create';
import { usePermissions } from '../../hooks/usePermissions';
import ExportButton from '../../components/ExportButton';
import { toast } from 'sonner';
import {
  Link2,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Copy,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Globe,
  Network,
  Zap,
  Activity,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const Enlaces = () => {
  const navigate = useNavigate();
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('assets', 'enlaces');

  const [enlaces, setEnlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [stats, setStats] = useState({ total: 0, by_tipo: [], by_rol: [] });
  const [sortConfig, setSortConfig] = useState({ field: 'denominacion', dir: 'asc' });
  const [formOptions, setFormOptions] = useState({ 
    sites: [], ubicaciones: [], tipos: [], roles: [], proveedores: [], estados: [] 
  });
  
  // Filter options (only values that exist in enlaces table)
  const [filterOptions, setFilterOptions] = useState({ 
    sites: [], tipos: [], roles: [], proveedores: [] 
  });
  
  // Filters
  const [filters, setFilters] = useState({ 
    search: '', site_id: '', tipo_id: '', rol_id: '', proveedor_id: '', estado_id: '' 
  });
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Modal para IPs adicionales
  const [ipModalOpen, setIpModalOpen] = useState(false);
  const [editingIp, setEditingIp] = useState(null);
  const [ipFormData, setIpFormData] = useState({
    ip_address: '', descripcion: '', destino: '', puerto: '', estado: 'activo'
  });
  const [deleteIpConfirm, setDeleteIpConfirm] = useState(null);
  
  const [formData, setFormData] = useState({
    denominacion: '', upload: '', download: '', tipo_enlace: '', rol: '',
    site: '', ubicacion: '', proveedor: '', description: '', nro_cliente: '',
    general_monitoring: false, external_monitoring: false, estado: '',
    costo: '', observations: '',
    // Nuevos campos de red
    ip_address: '', gateway: '', subnet_mask: '', cidr: '',
    dns_primary: '', dns_secondary: '', puerto_interface: ''
  });

  const fetchEnlaces = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pagination.page, limit: pagination.limit });
      if (filters.search) params.append('search', filters.search);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.tipo_id) params.append('tipo_id', filters.tipo_id);
      if (filters.rol_id) params.append('rol_id', filters.rol_id);
      if (filters.proveedor_id) params.append('proveedor_id', filters.proveedor_id);
      if (filters.estado_id) params.append('estado_id', filters.estado_id);
      
      const response = await api.get(`/assets/enlaces?${params}`);
      setEnlaces(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar enlaces');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/enlaces/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/enlaces/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/enlaces/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  useEffect(() => { fetchFormOptions(); fetchFilterOptions(); fetchStats(); }, []);
  useEffect(() => { fetchEnlaces(); }, [pagination.page, pagination.limit]);
  
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchEnlaces();
    }, 800);
    return () => clearTimeout(t);
  }, [filters]);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      denominacion: '', upload: '', download: '', tipo_enlace: '', rol: '',
      site: '', ubicacion: '', proveedor: '', description: '', nro_cliente: '',
      general_monitoring: false, external_monitoring: false, estado: '',
      costo: '', observations: '',
      ip_address: '', gateway: '', subnet_mask: '', cidr: '',
      dns_primary: '', dns_secondary: '', puerto_interface: ''
    });
    setModalOpen(true);
  };

  const handleView = async (en) => {
    try {
      const response = await api.get(`/assets/enlaces/${en.id}`);
      setViewingItem(response.data);
      setViewModalOpen(true);
    } catch (error) {
      toast.error('Error al cargar enlace');
    }
  };

  const handleEdit = async (en) => {
    try {
      const response = await api.get(`/assets/enlaces/${en.id}`);
      const data = response.data;
      setEditingItem(data);
      setFormData({
        denominacion: data.denominacion || '',
        upload: data.upload || '',
        download: data.download || '',
        tipo_enlace: data.tipo?.id?.toString() || '',
        rol: data.rol?.id?.toString() || '',
        site: data.site?.id?.toString() || '',
        ubicacion: data.ubicacion?.id?.toString() || '',
        proveedor: data.proveedor?.id?.toString() || '',
        description: data.description || '',
        nro_cliente: data.nro_cliente || '',
        general_monitoring: data.general_monitoring || false,
        external_monitoring: data.external_monitoring || false,
        estado: data.estado?.id?.toString() || '',
        costo: data.costo?.toString() || '',
        observations: data.observations || '',
        // Nuevos campos de red
        ip_address: data.ip_address || '',
        gateway: data.gateway || '',
        subnet_mask: data.subnet_mask || '',
        cidr: data.cidr?.toString() || '',
        dns_primary: data.dns_primary || '',
        dns_secondary: data.dns_secondary || '',
        puerto_interface: data.puerto_interface || ''
      });
      setModalOpen(true);
    } catch (error) {
      toast.error('Error al cargar enlace');
    }
  };

  const handleCopy = async (en) => {
    try {
      const response = await api.get(`/assets/enlaces/${en.id}`);
      const data = response.data;
      setEditingItem(null); // Not editing, creating new
      setFormData({
        denominacion: '', // Clear unique field
        upload: data.upload || '',
        download: data.download || '',
        tipo_enlace: data.tipo?.id?.toString() || '',
        rol: data.rol?.id?.toString() || '',
        site: data.site?.id?.toString() || '',
        ubicacion: data.ubicacion?.id?.toString() || '',
        proveedor: data.proveedor?.id?.toString() || '',
        description: '',
        nro_cliente: '', // Clear unique field
        general_monitoring: data.general_monitoring || false,
        external_monitoring: data.external_monitoring || false,
        estado: data.estado?.id?.toString() || '',
        costo: data.costo?.toString() || '',
        observations: '',
        // Nuevos campos de red - copiar
        ip_address: '', // Clear IP for copy
        gateway: data.gateway || '',
        subnet_mask: data.subnet_mask || '',
        cidr: data.cidr?.toString() || '',
        dns_primary: data.dns_primary || '',
        dns_secondary: data.dns_secondary || '',
        puerto_interface: ''
      });
      setModalOpen(true);
      toast.info('Datos copiados. Complete los campos unicos.');
    } catch (error) {
      toast.error('Error al copiar enlace');
    }
  };

  const handleSave = async () => {
    if (!formData.denominacion.trim()) {
      toast.error('La denominacion es requerida');
      return;
    }
    if (!formData.tipo_enlace || !formData.rol || !formData.site || !formData.proveedor) {
      toast.error('Tipo, Rol, Site y Proveedor son requeridos');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        denominacion: formData.denominacion,
        upload: formData.upload || null,
        download: formData.download || null,
        tipo_enlace: parseInt(formData.tipo_enlace),
        rol: parseInt(formData.rol),
        site: parseInt(formData.site),
        ubicacion: formData.ubicacion ? parseInt(formData.ubicacion) : null,
        proveedor: parseInt(formData.proveedor),
        description: formData.description || null,
        nro_cliente: formData.nro_cliente || null,
        general_monitoring: formData.general_monitoring,
        external_monitoring: formData.external_monitoring,
        estado: formData.estado ? parseInt(formData.estado) : null,
        costo: formData.costo ? parseInt(formData.costo) : null,
        observations: formData.observations || null,
        // Nuevos campos de red
        ip_address: formData.ip_address || null,
        gateway: formData.gateway || null,
        subnet_mask: formData.subnet_mask || null,
        cidr: formData.cidr ? parseInt(formData.cidr) : null,
        dns_primary: formData.dns_primary || null,
        dns_secondary: formData.dns_secondary || null,
        puerto_interface: formData.puerto_interface || null
      };

      if (editingItem) {
        await api.put(`/assets/enlaces/${editingItem.id}`, payload);
        toast.success('Enlace actualizado exitosamente');
      } else {
        await api.post('/assets/enlaces', payload);
        toast.success('Enlace creado exitosamente');
      }
      setModalOpen(false);
      fetchEnlaces();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (en) => {
    try {
      await api.delete(`/assets/enlaces/${en.id}`);
      toast.success('Enlace eliminado exitosamente');
      setDeleteConfirm(null);
      fetchEnlaces();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // ============ CRUD para IPs Adicionales ============
  const handleAddIp = () => {
    setEditingIp(null);
    setIpFormData({ ip_address: '', descripcion: '', destino: '', puerto: '', estado: 'activo' });
    setIpModalOpen(true);
  };

  const handleEditIp = (ip) => {
    setEditingIp(ip);
    setIpFormData({
      ip_address: ip.ip_address || '',
      descripcion: ip.descripcion || '',
      destino: ip.destino || '',
      puerto: ip.puerto || '',
      estado: ip.estado || 'activo'
    });
    setIpModalOpen(true);
  };

  const handleSaveIp = async () => {
    if (!ipFormData.ip_address.trim()) {
      toast.error('La IP es requerida');
      return;
    }
    setSaving(true);
    try {
      if (editingIp) {
        await api.put(`/assets/enlaces/${viewingItem.id}/ips/${editingIp.id}`, ipFormData);
        toast.success('IP actualizada');
      } else {
        await api.post(`/assets/enlaces/${viewingItem.id}/ips`, ipFormData);
        toast.success('IP agregada');
      }
      setIpModalOpen(false);
      // Refresh viewingItem
      const response = await api.get(`/assets/enlaces/${viewingItem.id}`);
      setViewingItem(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar IP');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIp = async (ip) => {
    try {
      await api.delete(`/assets/enlaces/${viewingItem.id}/ips/${ip.id}`);
      toast.success('IP eliminada');
      setDeleteIpConfirm(null);
      // Refresh viewingItem
      const response = await api.get(`/assets/enlaces/${viewingItem.id}`);
      setViewingItem(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar IP');
    }
  };

  const handleToggleMonitoring = async (en, field) => {
    if (!canUpdate) return;
    try {
      const payload = { [field]: !en[field] };
      await api.patch(`/assets/enlaces/${en.id}/monitoring`, payload);
      setEnlaces(prev => prev.map(e => 
        e.id === en.id ? { ...e, [field]: !e[field] } : e
      ));
      // Update viewingItem if open
      if (viewingItem?.id === en.id) {
        setViewingItem(prev => ({ ...prev, [field]: !prev[field] }));
      }
      toast.success('Monitoreo actualizado');
    } catch (error) {
      toast.error('Error al actualizar monitoreo');
    }
  };

  // Tipo badge color
  const getTipoBadgeColor = (tipo) => {
    switch (tipo?.toUpperCase()) {
      case 'INET': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'L2L': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'MPLS': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  // Rol badge color
  const getRolBadgeColor = (rol) => {
    switch (rol?.toUpperCase()) {
      case 'PRIMARIO': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'SECUNDARIO': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'BACKUP': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="enlaces-page">
        {/* Header - Unified pattern */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Enlaces</h1>
            <p className="page-subtitle">Gestion de enlaces de comunicacion</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/enlaces"
              filters={{ search: filters.search }}
              filename="enlaces"
            />
            <Button
              className={`btn btn-primary ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={canCreate ? handleCreate : undefined}
              disabled={!canCreate}
              data-testid="add-enlace-btn"
            >
              <Plus className="h-4 w-4" />
              Nuevo Enlace
            </Button>
          </div>
        </div>

        {/* Stats - 3 columns unified pattern */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="enlaces-stats">
          <div className="stats-card flex items-center gap-3" data-testid="stats-total">
            <Link2 className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total enlaces</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3">
            <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.by_tipo?.find(t => t.tipo === 'INET')?.count || 0}</div>
              <div className="stats-card-label">INET</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-blue-500">{stats.by_tipo?.filter(t => t.tipo !== 'INET').reduce((sum, t) => sum + t.count, 0) || 0}</div>
              <div className="stats-card-label">L2L/MPLS</div>
            </div>
          </div>
        </div>

        {/* Table with inline filters - Unified pattern */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      {/* Headers Row */}
                      <tr>
                        <th>DENOMINACION</th>
                        <th>TIPO</th>
                        <th>ROL</th>
                        <th className="hidden lg:table-cell">VELOCIDAD</th>
                        <th className="hidden lg:table-cell">IP</th>
                        <th className="hidden lg:table-cell">SITE</th>
                        <th className="hidden lg:table-cell">PROVEEDOR</th>
                        <th className="w-28 text-right">ACCIONES</th>
                      </tr>
                      {/* Filters Row */}
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Buscar en todos los campos..."
                            value={filters.search}
                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-denominacion"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Select value={filters.tipo_id || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, tipo_id: v === 'all' ? '' : v }))}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {filterOptions.tipos?.map(t => (
                                <SelectItem key={t.id} value={t.id.toString()}>{t.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4">
                          <Select value={filters.rol_id || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, rol_id: v === 'all' ? '' : v }))}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {filterOptions.roles?.map(r => (
                                <SelectItem key={r.id} value={r.id.toString()}>{r.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="hidden lg:table-cell py-2 px-4"></th>
                        <th className="hidden lg:table-cell py-2 px-4">
                          {/* IP filter - empty for now */}
                        </th>
                        <th className="hidden lg:table-cell py-2 px-4">
                          <Select value={filters.site_id || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, site_id: v === 'all' ? '' : v }))}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {filterOptions.sites?.map(s => (
                                <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="hidden lg:table-cell py-2 px-4">
                          <Select value={filters.proveedor_id || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, proveedor_id: v === 'all' ? '' : v }))}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {filterOptions.proveedores?.map(p => (
                                <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="w-28"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {enlaces.length === 0 ? (
                        <tr>
                          <td colSpan={8}>
                            <div className="empty-state">
                              <Link2 className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron enlaces</p>
                            </div>
                          </td>
                        </tr>
                      ) : enlaces.map((en) => (
                        <tr key={en.id} data-testid={`row-enlace-${en.id}`}>
                          <td>
                            <span className="font-medium text-primary">{en.denominacion}</span>
                          </td>
                          <td>
                            <span className={`badge ${getTipoBadgeColor(en.tipo?.name)}`}>
                              {en.tipo?.name || '-'}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${getRolBadgeColor(en.rol?.name)}`}>
                              {en.rol?.name || '-'}
                            </span>
                          </td>
                          <td className="hidden lg:table-cell font-mono text-sm text-muted-foreground">
                            {en.upload && en.download ? (
                              <span>{en.upload}/{en.download} MB</span>
                            ) : en.upload ? (
                              <span>{en.upload} MB</span>
                            ) : '-'}
                          </td>
                          <td className="hidden lg:table-cell font-mono text-sm text-muted-foreground">
                            {en.ip_address || '-'}
                          </td>
                          <td className="hidden lg:table-cell text-muted-foreground">{en.site?.name || '-'}</td>
                          <td className="hidden lg:table-cell text-muted-foreground">{en.proveedor?.name || '-'}</td>
                          <td className="w-28 text-right">
                            <div className="flex justify-end gap-0.5">
                              <button
                                onClick={() => handleView(en)}
                                className="action-btn"
                                title="Ver detalle"
                                data-testid={`view-enlace-${en.id}`}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => canUpdate && handleEdit(en)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                                disabled={!canUpdate}
                                title={canUpdate ? "Editar" : "Sin permiso"}
                                data-testid={`edit-enlace-${en.id}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => canCreate && handleCopy(en)}
                                className={`action-btn action-btn-copy ${!canCreate ? 'opacity-50' : ''}`}
                                disabled={!canCreate}
                                title={canCreate ? "Copiar registro" : "Sin permiso"}
                                data-testid={`copy-enlace-${en.id}`}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteConfirm(en)}
                                className={`action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title={canDelete ? "Eliminar" : "Sin permiso"}
                                data-testid={`delete-enlace-${en.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-border/30">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-muted-foreground">
                      {pagination.total > 0 ? `${((pagination.page - 1) * pagination.limit) + 1} - ${Math.min(pagination.page * pagination.limit, pagination.total)} de ${pagination.total}` : '0 resultados'}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mostrar:</span>
                      <Select
                        value={pagination.limit.toString()}
                        onValueChange={(value) => setPagination(p => ({ ...p, limit: parseInt(value), page: 1 }))}
                      >
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1} className="h-8 w-8" data-testid="prev-page">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm px-2">{pagination.page} / {pagination.pages || 1}</span>
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page >= pagination.pages} className="h-8 w-8" data-testid="next-page">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="modal-content max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="h-5 w-5 text-primary" />
                {editingItem ? 'Editar Enlace' : 'Nuevo Enlace'}
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4 grid grid-cols-2 gap-4">
              {/* Row 1 */}
              <div className="col-span-2">
                <Label className="text-sm font-medium">Denominacion *</Label>
                <Input
                  value={formData.denominacion}
                  onChange={(e) => setFormData(prev => ({ ...prev, denominacion: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="Nombre del enlace"
                  data-testid="input-enlace-denominacion"
                />
              </div>
              
              {/* Row 2 */}
              <div>
                <Label className="text-sm font-medium">Tipo *</Label>
                <SelectWithCreate
                  value={formData.tipo_enlace}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, tipo_enlace: v }))}
                  options={formOptions.tipos?.map(t => ({ id: t.id, nombre: t.name })) || []}
                  placeholder="Seleccionar tipo"
                  createEndpoint="/assets/enlaces/quick-create/tipo"
                  entityName="Tipo de Enlace"
                  createFields={{
                    name: { label: "Tipo", placeholder: "Ej: MPLS" },
                    description: { label: "Descripción", placeholder: "Descripción (opcional)" }
                  }}
                  onCreated={(newItem) => {
                    fetchFormOptions();
                    setFormData(prev => ({ ...prev, tipo_enlace: String(newItem.id) }));
                  }}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Rol *</Label>
                <SelectWithCreate
                  value={formData.rol}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, rol: v }))}
                  options={formOptions.roles?.map(r => ({ id: r.id, nombre: r.name })) || []}
                  placeholder="Seleccionar rol"
                  createEndpoint="/assets/enlaces/quick-create/rol"
                  entityName="Rol"
                  createFields={{
                    name: { label: "Rol", placeholder: "Ej: PRINCIPAL" },
                    description: { label: "Descripción", placeholder: "Descripción (opcional)" }
                  }}
                  onCreated={(newItem) => {
                    fetchFormOptions();
                    setFormData(prev => ({ ...prev, rol: String(newItem.id) }));
                  }}
                  className="mt-1.5"
                />
              </div>
              
              {/* Row 3 */}
              <div>
                <Label className="text-sm font-medium">Site *</Label>
                <SelectWithCreate
                  value={formData.site}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, site: v }))}
                  options={formOptions.sites?.map(s => ({ id: s.id, nombre: s.name })) || []}
                  placeholder="Seleccionar site"
                  createEndpoint="/assets/enlaces/quick-create/site"
                  entityName="Site"
                  createFields={{
                    name: { label: "Nombre", placeholder: "Ej: TZ_NUEVO" },
                    description: { label: "Descripción", placeholder: "Descripción (opcional)" }
                  }}
                  onCreated={(newItem) => {
                    fetchFormOptions();
                    setFormData(prev => ({ ...prev, site: String(newItem.id) }));
                  }}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Ubicacion *</Label>
                <SelectWithCreate
                  value={formData.ubicacion}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, ubicacion: v }))}
                  options={formOptions.ubicaciones?.map(u => ({ id: u.id, nombre: u.name })) || []}
                  placeholder="Seleccionar ubicacion"
                  createEndpoint="/assets/enlaces/quick-create/ubicacion"
                  entityName="Ubicación"
                  createFields={{
                    name: { label: "Ubicación", placeholder: "Ej: DATACENTER" },
                    description: { label: "Descripción", placeholder: "Descripción (opcional)" }
                  }}
                  onCreated={(newItem) => {
                    fetchFormOptions();
                    setFormData(prev => ({ ...prev, ubicacion: String(newItem.id) }));
                  }}
                  className="mt-1.5"
                />
              </div>
              
              {/* Row 4 */}
              <div>
                <Label className="text-sm font-medium">Proveedor *</Label>
                <SelectWithCreate
                  value={formData.proveedor}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, proveedor: v }))}
                  options={formOptions.proveedores?.map(p => ({ id: p.id, nombre: p.name })) || []}
                  placeholder="Seleccionar proveedor"
                  createEndpoint="/assets/enlaces/quick-create/proveedor"
                  entityName="Proveedor"
                  createFields={{
                    name: { label: "Proveedor", placeholder: "Ej: Telefonica" },
                    description: { label: "Descripción", placeholder: "Descripción (opcional)" }
                  }}
                  onCreated={(newItem) => {
                    fetchFormOptions();
                    setFormData(prev => ({ ...prev, proveedor: String(newItem.id) }));
                  }}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Estado</Label>
                <Select value={formData.estado} onValueChange={(v) => setFormData(prev => ({ ...prev, estado: v }))}>
                  <SelectTrigger className="input-subtle mt-1.5">
                    <SelectValue placeholder="Seleccionar estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.estados.map(e => (
                      <SelectItem key={e.id} value={e.id.toString()}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Row 5 - Velocidad */}
              <div>
                <Label className="text-sm font-medium">Upload (MB)</Label>
                <Input
                  value={formData.upload}
                  onChange={(e) => setFormData(prev => ({ ...prev, upload: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="100"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Download (MB)</Label>
                <Input
                  value={formData.download}
                  onChange={(e) => setFormData(prev => ({ ...prev, download: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="100"
                />
              </div>
              
              {/* Row 5b - Configuracion de Red */}
              <div className="col-span-2 mt-2">
                <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2 mb-3">
                  <Network className="h-4 w-4" strokeWidth={1.5} />
                  Configuracion de Red
                </h4>
              </div>
              <div>
                <Label className="text-sm font-medium">IP Principal</Label>
                <Input
                  value={formData.ip_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))}
                  className="input-subtle mt-1.5 font-mono"
                  placeholder="190.210.247.118"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Gateway</Label>
                <Input
                  value={formData.gateway}
                  onChange={(e) => setFormData(prev => ({ ...prev, gateway: e.target.value }))}
                  className="input-subtle mt-1.5 font-mono"
                  placeholder="190.210.247.254"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Mascara</Label>
                <Input
                  value={formData.subnet_mask}
                  onChange={(e) => setFormData(prev => ({ ...prev, subnet_mask: e.target.value }))}
                  className="input-subtle mt-1.5 font-mono"
                  placeholder="255.255.255.0"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">CIDR</Label>
                <Input
                  type="number"
                  value={formData.cidr}
                  onChange={(e) => setFormData(prev => ({ ...prev, cidr: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="24"
                  min="0"
                  max="32"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">DNS Primario</Label>
                <Input
                  value={formData.dns_primary}
                  onChange={(e) => setFormData(prev => ({ ...prev, dns_primary: e.target.value }))}
                  className="input-subtle mt-1.5 font-mono"
                  placeholder="200.69.193.1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">DNS Secundario</Label>
                <Input
                  value={formData.dns_secondary}
                  onChange={(e) => setFormData(prev => ({ ...prev, dns_secondary: e.target.value }))}
                  className="input-subtle mt-1.5 font-mono"
                  placeholder="190.210.156.1"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-sm font-medium">Puerto / Interface</Label>
                <Input
                  value={formData.puerto_interface}
                  onChange={(e) => setFormData(prev => ({ ...prev, puerto_interface: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="LAN3, WAN1, Port1..."
                />
              </div>
              
              {/* Row 6 */}
              <div>
                <Label className="text-sm font-medium">Nro Cliente</Label>
                <Input
                  value={formData.nro_cliente}
                  onChange={(e) => setFormData(prev => ({ ...prev, nro_cliente: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="Numero de cliente"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Costo Mensual</Label>
                <Input
                  type="number"
                  value={formData.costo}
                  onChange={(e) => setFormData(prev => ({ ...prev, costo: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="0"
                />
              </div>
              
              {/* Row 7 - Description */}
              <div className="col-span-2">
                <Label className="text-sm font-medium">Descripcion</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="Descripcion del enlace"
                />
              </div>
              
              {/* Row 8 - Monitoring */}
              <div className="col-span-2 grid grid-cols-2 gap-4 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-primary" />
                    <Label className="text-sm">Monitoreo General</Label>
                  </div>
                  <Switch
                    checked={formData.general_monitoring}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, general_monitoring: v }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-primary" />
                    <Label className="text-sm">Monitoreo Externo</Label>
                  </div>
                  <Switch
                    checked={formData.external_monitoring}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, external_monitoring: v }))}
                  />
                </div>
              </div>
              
              {/* Observations */}
              <div className="col-span-2">
                <Label className="text-sm font-medium">Observaciones</Label>
                <Input
                  value={formData.observations}
                  onChange={(e) => setFormData(prev => ({ ...prev, observations: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="Notas adicionales..."
                />
              </div>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Modal */}
        <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
          <DialogContent className="modal-content max-w-2xl">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="h-5 w-5 text-primary" />
                {viewingItem?.denominacion}
              </DialogTitle>
            </DialogHeader>
            {viewingItem && (
              <div className="modal-body py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Tipo</p>
                    <span className={`badge ${getTipoBadgeColor(viewingItem.tipo?.name)}`}>
                      {viewingItem.tipo?.name}
                    </span>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Rol</p>
                    <span className={`badge ${getRolBadgeColor(viewingItem.rol?.name)}`}>
                      {viewingItem.rol?.name}
                    </span>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Site</p>
                    <p className="font-medium">{viewingItem.site?.name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Ubicacion</p>
                    <p className="font-medium">{viewingItem.ubicacion?.name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Proveedor</p>
                    <p className="font-medium">{viewingItem.proveedor?.name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Nro Cliente</p>
                    <p className="font-mono">{viewingItem.nro_cliente || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Velocidad</p>
                    <p className="font-mono">
                      {viewingItem.upload && viewingItem.download 
                        ? `${viewingItem.upload}/${viewingItem.download} MB`
                        : viewingItem.upload ? `${viewingItem.upload} MB` : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Costo Mensual</p>
                    <p className="font-medium">{viewingItem.costo ? `$${viewingItem.costo.toLocaleString()}` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Estado</p>
                    <p className="font-medium">{viewingItem.estado?.name || '-'}</p>
                  </div>
                </div>
                {viewingItem.description && (
                  <div className="pt-3 border-t border-border/30">
                    <p className="text-muted-foreground text-sm">Descripcion</p>
                    <p className="mt-1">{viewingItem.description}</p>
                  </div>
                )}
                
                {/* Seccion de Red - Siempre visible */}
                <div className="pt-3 border-t border-border/30">
                  <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2 mb-3">
                    <Network className="h-4 w-4" strokeWidth={1.5} />
                    Configuracion de Red
                  </h4>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    <div>
                      <p className="text-muted-foreground text-xs">IP Principal</p>
                      <p className="font-mono text-sm">{viewingItem.ip_address || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Gateway</p>
                      <p className="font-mono text-sm">{viewingItem.gateway || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Mascara</p>
                      <p className="font-mono text-sm">
                        {viewingItem.subnet_mask || '-'}
                        {viewingItem.cidr && <span className="text-muted-foreground"> (/{viewingItem.cidr})</span>}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Puerto/Interface</p>
                      <p className="font-medium text-sm">{viewingItem.puerto_interface || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">DNS Primario</p>
                      <p className="font-mono text-sm">{viewingItem.dns_primary || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">DNS Secundario</p>
                      <p className="font-mono text-sm">{viewingItem.dns_secondary || '-'}</p>
                    </div>
                  </div>
                </div>
                
                {/* IPs Adicionales */}
                <div className="pt-3 border-t border-border/30">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                      <Globe className="h-4 w-4" strokeWidth={1.5} />
                      IPs Adicionales ({viewingItem.ips_adicionales?.length || 0})
                    </h4>
                    {canCreate && (
                      <Button size="sm" variant="outline" onClick={handleAddIp} className="h-7 text-xs">
                        <Plus className="h-3 w-3 mr-1" />
                        Agregar IP
                      </Button>
                    )}
                  </div>
                  {viewingItem.ips_adicionales && viewingItem.ips_adicionales.length > 0 ? (
                    <div className="overflow-hidden rounded-lg border border-border/50">
                      <table className="data-table text-sm">
                        <thead>
                          <tr className="bg-muted/50">
                            <th className="text-left py-2 px-3">IP</th>
                            <th className="text-left py-2 px-3">Descripcion</th>
                            <th className="text-left py-2 px-3">Puerto</th>
                            <th className="text-center py-2 px-3">Estado</th>
                            <th className="text-right py-2 px-3 w-20">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewingItem.ips_adicionales.map((ip) => (
                            <tr key={ip.id}>
                              <td className="font-mono py-1.5 px-3">{ip.ip_address}</td>
                              <td className="py-1.5 px-3 text-muted-foreground">{ip.descripcion || '-'}</td>
                              <td className="font-mono py-1.5 px-3">{ip.puerto || '-'}</td>
                              <td className="text-center py-1.5 px-3">
                                <span className={`badge ${ip.estado === 'activo' ? 'badge-success' : ip.estado === 'libre' ? 'badge-warning' : 'badge-default'}`}>
                                  {ip.estado}
                                </span>
                              </td>
                              <td className="text-right py-1.5 px-3">
                                <div className="flex justify-end gap-1">
                                  {canUpdate && (
                                    <button onClick={() => handleEditIp(ip)} className="action-btn action-btn-edit" title="Editar">
                                      <Edit className="h-3 w-3" />
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button onClick={() => setDeleteIpConfirm(ip)} className="action-btn-delete" title="Eliminar">
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No hay IPs adicionales registradas</p>
                  )}
                </div>
                
                {viewingItem.observations && (
                  <div className="pt-3 border-t border-border/30">
                    <p className="text-muted-foreground text-sm">Observaciones</p>
                    <p className="mt-1 whitespace-pre-wrap">{viewingItem.observations}</p>
                  </div>
                )}
                <div className="pt-3 border-t border-border/30 grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                    <div>
                      <Label className="text-sm font-medium">Monitoreo General</Label>
                      <p className="text-xs text-muted-foreground">Monitoreo basico de disponibilidad</p>
                    </div>
                    <Switch
                      checked={Boolean(viewingItem.general_monitoring)}
                      onCheckedChange={() => handleToggleMonitoring(viewingItem, 'general_monitoring')}
                      disabled={!canUpdate}
                      className="data-[state=checked]:bg-green-500"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                    <div>
                      <Label className="text-sm font-medium">Monitoreo Externo</Label>
                      <p className="text-xs text-muted-foreground">Monitoreo desde proveedor externo</p>
                    </div>
                    <Switch
                      checked={Boolean(viewingItem.external_monitoring)}
                      onCheckedChange={() => handleToggleMonitoring(viewingItem, 'external_monitoring')}
                      disabled={!canUpdate}
                      className="data-[state=checked]:bg-green-500"
                    />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setViewModalOpen(false)}>Cerrar</Button>
              {canUpdate && (
                <Button className="btn btn-primary" onClick={() => { setViewModalOpen(false); handleEdit(viewingItem); }}>
                  Editar
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminacion
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4">
              <p className="text-muted-foreground">
                Desea eliminar el enlace <span className="font-semibold text-foreground">{deleteConfirm?.denominacion}</span>?
              </p>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
              <Button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal para Agregar/Editar IP */}
        <Dialog open={ipModalOpen} onOpenChange={setIpModalOpen}>
          <DialogContent className="modal-content max-w-md" aria-describedby="ip-modal-desc">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                {editingIp ? 'Editar IP' : 'Agregar IP'}
              </DialogTitle>
            </DialogHeader>
            <p id="ip-modal-desc" className="sr-only">Formulario para agregar o editar IP adicional</p>
            <div className="modal-body py-4 space-y-4">
              <div>
                <Label className="text-sm font-medium">Direccion IP *</Label>
                <Input
                  value={ipFormData.ip_address}
                  onChange={(e) => setIpFormData(prev => ({ ...prev, ip_address: e.target.value }))}
                  className="input-subtle mt-1.5 font-mono"
                  placeholder="192.168.1.100"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Descripcion</Label>
                <Input
                  value={ipFormData.descripcion}
                  onChange={(e) => setIpFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="NAT Servidor Web, VPN SSL, Libre..."
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Destino</Label>
                <Input
                  value={ipFormData.destino}
                  onChange={(e) => setIpFormData(prev => ({ ...prev, destino: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="Servicio VPN, Web Productivo..."
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Puerto</Label>
                <Input
                  value={ipFormData.puerto}
                  onChange={(e) => setIpFormData(prev => ({ ...prev, puerto: e.target.value }))}
                  className="input-subtle mt-1.5 font-mono"
                  placeholder="443, 80-443, FISICA..."
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Estado</Label>
                <Select value={ipFormData.estado} onValueChange={(v) => setIpFormData(prev => ({ ...prev, estado: v }))}>
                  <SelectTrigger className="input-subtle mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="libre">Libre</SelectItem>
                    <SelectItem value="reservado">Reservado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setIpModalOpen(false)}>Cancelar</Button>
              <Button className="btn btn-primary" onClick={handleSaveIp} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Confirmar Eliminar IP */}
        <Dialog open={!!deleteIpConfirm} onOpenChange={() => setDeleteIpConfirm(null)}>
          <DialogContent className="modal-content" aria-describedby="delete-ip-desc">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar IP
              </DialogTitle>
            </DialogHeader>
            <p id="delete-ip-desc" className="modal-body py-4 text-muted-foreground">
              Desea eliminar la IP <span className="font-mono font-semibold text-foreground">{deleteIpConfirm?.ip_address}</span>?
            </p>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setDeleteIpConfirm(null)}>Cancelar</Button>
              <Button className="btn btn-danger" onClick={() => handleDeleteIp(deleteIpConfirm)}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Enlaces;
